// =====================================================================
// Assessment Center — Grading domain: pure grading engine.
// Pure grading rules. No data access, no framework imports — this is
// the one place "how do we score things" lives, so it can be
// unit-tested in isolation and reused unchanged once attempts-store.ts
// is swapped for real D1-backed Worker routes.
// =====================================================================
import type { ExamType, QuestionType } from "../types/db";

export interface GradableAnswer {
  exam_question_id: string;
  question_type: QuestionType;
  points_possible: number;
  selected_answer_id: string | null;
  correct_answer_id: string | null; // null for essay questions
  essay_answer_text: string | null;
}

export interface GradedAnswer {
  exam_question_id: string;
  is_correct: boolean | null; // null = not auto-gradable (essay)
  points_awarded: number | null; // null = not yet gradable (essay, pending review)
}

/** Multiple choice is graded the instant it's submitted: exact match against the answer key. */
export function gradeMultipleChoice(answer: GradableAnswer): GradedAnswer {
  const isCorrect =
    answer.selected_answer_id !== null && answer.selected_answer_id === answer.correct_answer_id;
  return {
    exam_question_id: answer.exam_question_id,
    is_correct: isCorrect,
    points_awarded: isCorrect ? answer.points_possible : 0,
  };
}

/** Essay questions are never auto-graded — they always wait for a human reviewer. */
export function essayAwaitsReview(answer: GradableAnswer): GradedAnswer {
  return { exam_question_id: answer.exam_question_id, is_correct: null, points_awarded: null };
}

/** Routes a single answer to the right grading rule by question type. */
export function gradeAnswer(answer: GradableAnswer): GradedAnswer {
  return answer.question_type === "multiple_choice"
    ? gradeMultipleChoice(answer)
    : essayAwaitsReview(answer);
}

/**
 * What should the attempt's status become right after submission, per
 * the exam-type rules for this phase:
 *   - Multiple Choice exam  → graded immediately (nothing left pending)
 *   - Essay exam            → stays "submitted" (== Waiting for Review)
 *   - Mixed exam            → MCQ graded now, but essay(s) still pending,
 *                              so the attempt also stays "submitted"
 * `exam_attempts.status` has no separate "waiting_for_review" value in
 * the schema — "submitted" already means exactly that for any
 * exam containing an essay section, so no schema change is needed.
 */
export function statusAfterSubmission(examType: ExamType): "submitted" | "graded" {
  return examType === "multiple_choice" ? "graded" : "submitted";
}

export interface ScoreTotals {
  points_earned: number;
  points_possible: number;
}

/** Sums whatever points are currently known (essay points count once reviewed, not before). */
export function sumKnownPoints(graded: GradedAnswer[], possibleByQuestion: Record<string, number>): ScoreTotals {
  let earned = 0;
  let possible = 0;
  for (const g of graded) {
    possible += possibleByQuestion[g.exam_question_id] ?? 0;
    earned += g.points_awarded ?? 0;
  }
  return { points_earned: earned, points_possible: possible };
}

/** True once every essay answer on the attempt has a completed review. */
export function allEssaysReviewed(essayAnswerCount: number, completedReviewCount: number): boolean {
  return essayAnswerCount > 0 ? essayAnswerCount === completedReviewCount : true;
}

export interface FinalGradeInput {
  totalEarned: number;
  totalPossible: number;
  passingScore: number | null;
}

export interface FinalGrade {
  total_score: number;
  max_score: number;
  percentage: number;
  passed: boolean;
}

/**
 * Computes the final grade once every gradable answer (MC always,
 * essay once reviewed) has a points value. `passed` defaults to true
 * when the exam has no passing_score configured — no threshold means
 * nothing to fail against.
 */
export function computeFinalGrade({ totalEarned, totalPossible, passingScore }: FinalGradeInput): FinalGrade {
  const maxScore = Math.max(totalPossible, 0.0001); // guard divide-by-zero; schema requires max_score > 0
  const percentage = Math.min(100, Math.max(0, (totalEarned / maxScore) * 100));
  return {
    total_score: totalEarned,
    max_score: totalPossible,
    percentage: Math.round(percentage * 100) / 100,
    passed: passingScore === null ? true : totalEarned >= passingScore,
  };
}
